import { Injectable } from '@angular/core';
import { Database, ref, set, update, get, query, orderByChild, equalTo } from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { LogService } from './log.service';
import { PlatformUser } from '../data-models/platform-user';
import { map, take, tap, catchError, first } from 'rxjs/operators';
import { from, Observable } from 'rxjs';
import { CrashReportingService } from './crash-reporting.service';
import { Router } from '@angular/router';
import { LocalService } from './local.service';
import { HttpClient } from '@angular/common/http';
import { GlobalsService } from './globals.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformUserService {
  sEndpoint: string = 'https://apis.zenithr.net/api/'; //'http://localhost:52619/api/';.
  coreAPI: string = 'https://apis.zenithr.net/api/'; //'http://localhost:52619/api/';

  constructor(
    private db: Database,
    public afAuth: Auth,
    private logService: LogService,
    private crashReportingService: CrashReportingService,
    private router: Router,
    private localService: LocalService,
    private http: HttpClient,
    private globals: GlobalsService
  ) {
    this.sEndpoint = this.globals.sEndpoint;
    this.coreAPI = this.globals.coreAPI;
  }



  updateUserPermissions(request: any) {
    return this.http.post(
      this.coreAPI + 'PlatformUser/AddPlatformUser',
      request
    );
  }

  fetchUserPermissions(request: any) {
    return this.http.post(
      this.coreAPI + 'PlatformUser/GetUserPermissions',
      request
    );
  }

  updateUserInfo(request: any) {
    return this.http.post(
      this.coreAPI + 'PlatformUser/UpdateUserInfo',
      request
    );
  }

  changePassword(request: { userKey: string, password: string }) {
    return this.http.post(`${this.coreAPI}PlatformUser/ChangePassword`, request);
  }

  changeEmail(request: { userKey: string, newEmail: string }) {
    return this.http.post(`${this.coreAPI}PlatformUser/ChangeEmail`, request);
  }

  createPlatformUser(user: PlatformUser) {
    const userRef = ref(this.db, `/PlatformUsers/${user.key}`);

    return from(set(userRef, user)).pipe(
      tap(() => {
        this.logService.submitLog(
          4,
          'AuthenticationService.createPlatformUser()',
          'Task Completed',
          { user }
        );
      }),
      catchError((error) => {
        this.logService.submitLog(
          4,
          'AuthenticationService.createPlatformUser()',
          'Task Failed',
          { user, error }
        );
        throw error; // rethrow for subscribers
      }),
    )
  }

  updatePlatformUser(user: PlatformUser) {
    if (!user.key) {
      throw new Error('PlatformUser key is required for update');
    }
    const userRef = ref(this.db, `/PlatformUsers/${user.key}`);
    return update(userRef, user);
  }

  // Fetch tenant users once
  fetchTenantPlatformUsersOnce(tenantKey: string): Observable<PlatformUser[]> {
    const usersQuery = query(
      ref(this.db, '/PlatformUsers'),
      orderByChild('tenantKey'),
      equalTo(tenantKey)
    );

    return from(get(usersQuery)).pipe(
      map(snapshot => {
        const users: PlatformUser[] = [];
        snapshot.forEach(childSnap => {
          users.push({ key: childSnap.key!, ...childSnap.val() } as PlatformUser);
        });
        return users;
      }),
      take(1)
    );
  }

  // Fetch single user by key
  fetchPlatformUser(userKey: string): Observable<PlatformUser | null> {
    const userRef = ref(this.db, `/PlatformUsers/${userKey}`);
    return from(get(userRef)).pipe(
      map(snapshot => {
        if (!snapshot.exists()) return null;
        return { key: snapshot.key!, ...snapshot.val() } as PlatformUser;
      })
    );
  }

  // Fetch all users for a tenant
  fetchAllTenantUsers(tenantKey: string): Observable<PlatformUser[]> {
    const usersQuery = query(
      ref(this.db, '/PlatformUsers'),
      orderByChild('tenantKey'),
      equalTo(tenantKey)
    );

    return from(get(usersQuery)).pipe(
      map(snapshot => {
        const users: PlatformUser[] = [];
        snapshot.forEach(childSnap => {
          users.push({ key: childSnap.key!, ...childSnap.val() } as PlatformUser);
        });
        return users;
      })
    );
  }

  // Fetch platform user by UID
  fetchPlatformUserByUid(uid: string): Observable<PlatformUser | null> {
    const usersQuery = query(
      ref(this.db, '/PlatformUsers'),
      orderByChild('uid'),
      equalTo(uid)
    );

    return from(get(usersQuery)).pipe(
      map(snapshot => {
        if (!snapshot.exists()) return null;
        let user: PlatformUser | null = null;
        snapshot.forEach(childSnap => {
          user = { key: childSnap.key!, ...childSnap.val() } as PlatformUser;
        });
        return user;
      })
    );
  }


  // Fetch all users once
  fetchAllUsers(): Observable<PlatformUser[]> {
    const usersRef = ref(this.db, '/PlatformUsers');
    return from(get(usersRef)).pipe(
      map(snapshot => {
        const users: PlatformUser[] = [];
        snapshot.forEach(childSnap => {
          users.push({ key: childSnap.key!, ...childSnap.val() } as PlatformUser);
        });
        return users;
      })
    );
  }

  sendResetPasswordLink(request: { email: string }) {
    return this.http.post(`${this.coreAPI}PlatformUser/SendResetPasswordLink`, request);
  }

  resetPassword(request: { token: string, newPassword: string }) {
    return this.http.post(`${this.coreAPI}PlatformUser/ResetPassword`, request);
  }

}
